import {
  BedDouble, Box, ChartNoAxesColumnIncreasing, CircleDot, Columns3,
  DoorOpen, Hash, PanelTop, RectangleHorizontal, Skull, Type, User, Users,
} from "lucide-react";
import { STAMP_CATALOG } from "@/src/core/engine/stampCatalog";
import { useT } from "@/src/i18n";
import { WallTypePicker } from "@/src/components/WallTypePicker";
import { MarkerTypePicker } from "@/src/components/MarkerTypePicker";
import { FloorTypePicker } from "@/src/components/FloorTypePicker";
import { useMapStore } from "@/src/store/useMapStore";
import type { StampAssetId } from "@/src/types/map";

const icons: Partial<Record<StampAssetId, typeof DoorOpen>> = {
  door: DoorOpen,
  portal: CircleDot,
  stairs: ChartNoAxesColumnIncreasing,
  table: PanelTop,
  bed: BedDouble,
  rug: RectangleHorizontal,
  crate: Box,
  marker: Columns3,
  text_label: Type,
  room_number: Hash,
  token_player: User,
  token_enemy: Skull,
  token_npc: Users,
};

export function StampDrawer() {
  const active = useMapStore((state) => state.selectedStampAsset);
  const setActive = useMapStore((state) => state.setSelectedStampAsset);
  const repeatPlacement = useMapStore((state) => state.repeatPlacement);
  const setRepeatPlacement = useMapStore((state) => state.setRepeatPlacement);
  const t = useT();
  const categories = ["Architecture", "Furniture", "Props", "Tokens"] as const;
  return (
    <aside className="flex max-h-full w-56 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1c1b18]/97 shadow-2xl shadow-black/30 backdrop-blur">
      <div className="shrink-0 border-b border-white/8 px-4 py-3">
        <p className="text-xs font-semibold text-stone-100">{t("stamps.title")}</p>
        <p className="mt-1 text-[10px] leading-4 text-stone-500">{t("stamps.subtitle")}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 p-3">
          {categories.map((category) => (
            <section key={category}>
              <p className="mb-1.5 px-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-600">{t(`category.${category}`)}</p>
              <div className="grid grid-cols-3 gap-1.5">
                {STAMP_CATALOG.filter((item) => item.category === category).map((item) => {
                  const Icon = icons[item.id] ?? Box;
                  const selected = active === item.id || (active === "chest" && item.id === "chest_wood");
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActive(item.id)}
                      className={`flex min-h-16 flex-col items-center justify-center rounded-xl border p-1.5 text-center transition ${selected ? "border-[#c3463d] bg-[#a8322c]/25 text-white" : "border-white/6 bg-white/[0.025] text-stone-400 hover:border-white/15 hover:bg-white/[0.06] hover:text-stone-100"}`}
                      aria-pressed={selected}
                      title={t(item.labelKey)}
                    >
                      <Icon size={18} strokeWidth={1.7} />
                      <span className="mt-1 text-[9px] font-medium leading-none">{t(item.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        {active === "door" && <WallTypePicker />}
        {active === "stairs" && <FloorTypePicker />}
        {active === "marker" && <MarkerTypePicker />}
      </div>
      <label className="flex shrink-0 cursor-pointer items-center justify-between gap-3 border-t border-white/8 px-4 py-3 text-[10px] text-stone-400">
        <span><strong className="block text-stone-200">{t("stamps.keepPlacing")}</strong>{t("stamps.keepPlacingBody")}</span>
        <input type="checkbox" checked={repeatPlacement} onChange={(event) => setRepeatPlacement(event.target.checked)} className="h-4 w-4 accent-[#b4362f]" />
      </label>
    </aside>
  );
}
